import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VideoRoom } from '@theraply/ui';
import { useJoinSession, useVideoRoom, useSessionStatus } from '@theraply/api-client';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Video session page for consumers
 * Handles joining and participating in video therapy sessions
 */
export const VideoSessionPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const [joinToken, setJoinToken] = useState<string | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const bookingIdNum = bookingId ? parseInt(bookingId, 10) : null;

  // Fetch video room information
  const { data: videoRoom, isLoading: isLoadingRoom, error: roomError } = useVideoRoom(bookingIdNum);

  // Join session mutation
  const joinMutation = useJoinSession();

  useEffect(() => {
    if (!bookingIdNum) {
      setError('Invalid booking ID');
      return;
    }

    // Auto-join when room is loaded
    if (videoRoom && !joinToken) {
      joinMutation.mutate(bookingIdNum, {
        onSuccess: (data) => {
          setJoinToken(data.token);
          setRoomUrl(data.roomUrl);
        },
        onError: (err: any) => {
          setError(err.response?.data?.message || 'Failed to join session');
        },
      });
    }
  }, [bookingIdNum, videoRoom, joinToken]);

  const handleLeave = () => {
    navigate('/appointments');
  };

  // Loading state
  if (isLoadingRoom || (!joinToken && !error)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold mb-2">Preparing your session...</h2>
          <p className="text-gray-400">This will just take a moment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || roomError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gray-800 rounded-lg shadow-xl p-8 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">Unable to Join Session</h2>
            <p className="text-gray-300 mb-6">
              {error || (roomError as any)?.response?.data?.message || 'An error occurred while joining the session'}
            </p>
            <button
              onClick={() => navigate('/appointments')}
              className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition"
            >
              Back to Appointments
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Video session active
  if (joinToken && roomUrl) {
    return (
      <VideoRoom
        roomUrl={roomUrl}
        token={joinToken}
        userName="Client"
        onLeave={handleLeave}
        onError={(err) => setError(err.message)}
        showControls={true}
        isHost={false}
      />
    );
  }

  return null;
};
